import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Laptop, ShieldCheck, ShieldOff } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { isApiError, type ApiError } from '@/lib/api/errors';
import { http } from '@/lib/api/http';
import { useAuth } from '@/lib/auth/auth';
import { formatRelative } from '@/lib/dates';
import { Button } from '@/ui/button';
import { Dialog } from '@/ui/dialog';
import { CopyButton, ErrorState, ProblemList, Skeleton } from '@/ui/feedback';
import { Field, Input } from '@/ui/form';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@/ui/surface';

const problemsOf = (e: unknown) => (isApiError(e) ? (((e as ApiError).details as { problems?: string[] } | undefined)?.problems ?? []) : []);
const msg = (e: unknown, fallback: string) => (isApiError(e) ? e.message : fallback);

// ------------------------------------------------------------------ password
export function PasswordForm({ forced, onDone }: { forced?: boolean; onDone?: () => void }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(''); const [next, setNext] = useState(''); const [again, setAgain] = useState('');
  const [error, setError] = useState<unknown>(null);
  const change = useMutation({
    mutationFn: () => http.post<{ sessionsEnded: number }>('/auth/change-password', { currentPassword: current, newPassword: next }),
    onSuccess: (r) => { toast.success(t('sec.passwordChanged', { n: r.sessionsEnded })); setCurrent(''); setNext(''); setAgain(''); onDone?.(); },
    onError: setError,
  });
  const mismatch = again.length > 0 && again !== next;
  return (
    <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); setError(null); change.mutate(); }}>
      {error != null && <div role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">{msg(error, t('errors.INTERNAL_ERROR'))}{problemsOf(error).length > 0 && <ProblemList problems={problemsOf(error)} />}</div>}
      <Field label={forced ? t('sec.temporaryPassword') : t('sec.currentPassword')} required>{(id) => <Input id={id} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}</Field>
      <Field label={t('sec.newPassword')} hint={t('sec.passwordRules')} required>{(id, d) => <Input id={id} aria-describedby={d} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}</Field>
      <Field label={t('sec.confirmPassword')} error={mismatch ? t('sec.mismatch') : undefined} required>{(id, d) => <Input id={id} aria-describedby={d} aria-invalid={mismatch} type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />}</Field>
      <div className="flex justify-end"><Button type="submit" variant="primary" disabled={!current || next.length < 10 || next !== again} loading={change.isPending}>{t('sec.changePassword')}</Button></div>
    </form>
  );
}

export function PasswordCard() {
  const { t } = useTranslation();
  return <Card><CardHeader><CardTitle>{t('sec.passwordTitle')}</CardTitle></CardHeader><CardBody><p className="mb-4 text-sm text-muted">{t('sec.passwordHint')}</p><PasswordForm /></CardBody></Card>;
}

/** Shown instead of the application while the account still has a temporary password (the server refuses everything else anyway). */
export function ForcedPasswordPage() {
  const { t } = useTranslation();
  const { reloadMe, logout } = useAuth();
  return (
    <main className="grid min-h-dvh place-items-center px-5 py-10">
      <div className="animate-in w-full max-w-sm space-y-6">
        <div className="space-y-1.5"><h1 className="text-2xl font-semibold tracking-tight">{t('sec.forcedTitle')}</h1><p className="text-sm text-muted">{t('sec.forcedHint')}</p></div>
        <PasswordForm forced onDone={() => void reloadMe()} />
        <button type="button" onClick={() => void logout()} className="text-sm text-muted underline-offset-2 hover:underline">{t('auth.signOut')}</button>
      </div>
    </main>
  );
}

/** Shown instead of the application while the user's role requires two-factor authentication and it is not set up yet. */
export function ForcedMfaPage() {
  const { t } = useTranslation();
  const { reloadMe, logout } = useAuth();
  const q = useQuery({ queryKey: ['auth', 'mfa'], queryFn: () => http.get<MfaStatus>('/auth/mfa') });
  return (
    <main className="grid min-h-dvh place-items-center px-5 py-10">
      <div className="animate-in w-full max-w-md space-y-6">
        <div className="space-y-1.5"><h1 className="text-2xl font-semibold tracking-tight">{t('sec.mfaForcedTitle')}</h1><p className="text-sm text-muted">{t('sec.mfaForcedHint')}</p></div>
        <MfaCard />
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={() => void logout()} className="text-sm text-muted underline-offset-2 hover:underline">{t('auth.signOut')}</button>
          <Button variant="primary" disabled={!q.data?.enabled} onClick={() => void reloadMe()}>{t('sec.mfaForcedContinue')}</Button>
        </div>
      </div>
    </main>
  );
}

// ------------------------------------------------------------------ sessions
interface Sess { id: string; createdAt: string; lastSeenAt: string; ip: string | null; userAgent: string | null; current: boolean }
const device = (ua: string | null) => {
  if (!ua) return '—';
  const os = /Windows/i.test(ua) ? 'Windows' : /Android/i.test(ua) ? 'Android' : /iPhone|iPad/i.test(ua) ? 'iOS' : /Mac OS/i.test(ua) ? 'macOS' : /Linux/i.test(ua) ? 'Linux' : '';
  const br = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : '';
  return [br, os].filter(Boolean).join(' · ') || ua.slice(0, 40);
};

export function SessionsCard() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['auth', 'sessions'], queryFn: () => http.get<Sess[]>('/auth/sessions') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
  const revoke = useMutation({ mutationFn: (id: string) => http.delete(`/auth/sessions/${id}`), onSuccess: () => { toast.success(t('purch.done')); void refresh(); }, onError: (e) => toast.error(msg(e, t('errors.INTERNAL_ERROR'))) });
  const others = useMutation({ mutationFn: () => http.post<{ revoked: number }>('/auth/sessions/revoke-others'), onSuccess: (r) => { toast.success(t('sec.sessionsEnded', { n: r.revoked })); void refresh(); }, onError: (e) => toast.error(msg(e, t('errors.INTERNAL_ERROR'))) });
  return (
    <Card>
      <CardHeader><CardTitle>{t('sec.sessionsTitle')}</CardTitle>{(q.data?.length ?? 0) > 1 && <Button size="sm" variant="ghost" loading={others.isPending} onClick={() => others.mutate()}>{t('sec.endOthers')}</Button>}</CardHeader>
      <CardBody className="p-0">
        {q.error ? <div className="p-5"><ErrorState error={q.error} compact /></div> : !q.data ? <div className="p-5"><Skeleton className="h-16" /></div> : (
          <ul className="divide-y divide-border">
            {q.data.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm">
                <div className="flex min-w-0 items-center gap-3"><Laptop className="size-4 shrink-0 text-muted" aria-hidden />
                  <div className="min-w-0"><p className="truncate font-medium">{device(s.userAgent)} {s.current && <Badge tone="success">{t('sec.thisDevice')}</Badge>}</p><p className="text-xs text-muted">{s.ip ?? '—'} · {t('sec.lastUsed', { when: formatRelative(s.lastSeenAt, i18n.language) })}</p></div></div>
                {!s.current && <Button size="sm" variant="ghost" className="text-danger hover:text-danger" loading={revoke.isPending && revoke.variables === s.id} onClick={() => revoke.mutate(s.id)}>{t('sec.endSession')}</Button>}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

// ------------------------------------------------------------------ two-factor authentication
interface MfaStatus { enabled: boolean; pendingSetup: boolean; recoveryCodesLeft: number }

export function MfaCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['auth', 'mfa'], queryFn: () => http.get<MfaStatus>('/auth/mfa') });
  const [dialog, setDialog] = useState<'setup' | 'disable' | 'codes' | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['auth', 'mfa'] });
  return (
    <>
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2">{q.data?.enabled ? <ShieldCheck className="size-4 text-success" aria-hidden /> : <ShieldOff className="size-4 text-muted" aria-hidden />}{t('sec.mfaTitle')}</CardTitle>{q.data && <Badge tone={q.data.enabled ? 'success' : 'neutral'} dot>{q.data.enabled ? t('sec.mfaOn') : t('sec.mfaOff')}</Badge>}</CardHeader>
        <CardBody className="space-y-4 text-sm">
          <p className="text-muted">{t('sec.mfaHint')}</p>
          {q.error ? <ErrorState error={q.error} compact /> : !q.data ? <Skeleton className="h-10" /> : q.data.enabled ? (
            <div className="space-y-3">
              <p>{t('sec.recoveryLeft', { n: q.data.recoveryCodesLeft })}{q.data.recoveryCodesLeft <= 2 && <span className="ms-2 text-warning">{t('sec.recoveryLow')}</span>}</p>
              <div className="flex flex-wrap gap-2"><Button variant="secondary" onClick={() => setDialog('codes')}>{t('sec.newRecovery')}</Button><Button variant="ghost" className="text-danger hover:text-danger" onClick={() => setDialog('disable')}>{t('sec.disableMfa')}</Button></div>
            </div>
          ) : <Button variant="primary" onClick={() => setDialog('setup')}>{t('sec.enableMfa')}</Button>}
        </CardBody>
      </Card>
      <SetupDialog open={dialog === 'setup'} onClose={() => setDialog(null)} onDone={(c) => { setDialog(null); setCodes(c); void refresh(); }} />
      <VerifyDialog open={dialog === 'disable'} title={t('sec.disableMfa')} hint={t('sec.disableHint')} confirm={t('sec.disableMfa')} danger onClose={() => setDialog(null)}
        run={(password, code) => http.post('/auth/mfa/disable', { password, code }).then(() => { toast.success(t('sec.mfaDisabled')); void refresh(); })} />
      <VerifyDialog open={dialog === 'codes'} title={t('sec.newRecovery')} hint={t('sec.newRecoveryHint')} confirm={t('sec.newRecovery')} onClose={() => setDialog(null)}
        run={(password, code) => http.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', { password, code }).then((r) => { setCodes(r.recoveryCodes); void refresh(); })} />
      <RecoveryDialog codes={codes} onClose={() => setCodes(null)} />
    </>
  );
}

function SetupDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: (codes: string[]) => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState(''); const [code, setCode] = useState('');
  const [secret, setSecret] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [qr, setQr] = useState(''); const [error, setError] = useState<unknown>(null);
  useEffect(() => { if (open) { setPassword(''); setCode(''); setSecret(null); setQr(''); setError(null); } }, [open]);
  useEffect(() => { if (secret) void QRCode.toDataURL(secret.otpauthUrl, { margin: 1, width: 192 }).then(setQr).catch(() => setQr('')); }, [secret]);
  const start = useMutation({ mutationFn: () => http.post<{ secret: string; otpauthUrl: string }>('/auth/mfa/setup', { password }), onSuccess: setSecret, onError: setError });
  const enable = useMutation({ mutationFn: () => http.post<{ recoveryCodes: string[] }>('/auth/mfa/enable', { code }), onSuccess: (r) => onDone(r.recoveryCodes), onError: setError });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title={t('sec.enableMfa')} description={secret ? t('sec.scanHint') : t('sec.setupPasswordHint')}
      footer={<><Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>{!secret ? <Button variant="primary" disabled={!password} loading={start.isPending} onClick={() => { setError(null); start.mutate(); }}>{t('sec.continue')}</Button> : <Button variant="primary" disabled={code.length < 6} loading={enable.isPending} onClick={() => { setError(null); enable.mutate(); }}>{t('sec.activate')}</Button>}</>}>
      <div className="grid gap-4">
        {error != null && <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">{msg(error, t('errors.INTERNAL_ERROR'))}</p>}
        {!secret ? <Field label={t('sec.currentPassword')} required>{(id) => <Input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />}</Field> : (
          <>
            <div className="flex flex-wrap items-center gap-4">
              {qr ? <img src={qr} alt={t('sec.qrAlt')} className="size-48 rounded-lg border border-border bg-white p-1" /> : <Skeleton className="size-48" />}
              <div className="min-w-0 flex-1 space-y-2 text-sm"><p className="text-muted">{t('sec.manualKey')}</p><div className="flex items-center gap-2 rounded-md border border-border bg-surface-2 p-2"><code className="break-all font-mono text-xs">{secret.secret}</code><CopyButton value={secret.secret} /></div></div>
            </div>
            <Field label={t('sec.firstCode')} hint={t('sec.firstCodeHint')} required>{(id, d) => <Input id={id} aria-describedby={d} inputMode="numeric" autoComplete="one-time-code" className="tnum w-40 text-center text-lg tracking-widest" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />}</Field>
          </>
        )}
      </div>
    </Dialog>
  );
}

function VerifyDialog({ open, title, hint, confirm, danger, onClose, run }: { open: boolean; title: string; hint: string; confirm: string; danger?: boolean; onClose: () => void; run: (password: string, code: string) => Promise<unknown> }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState(''); const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPassword(''); setCode(''); setError(null); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title={title} description={hint}
      footer={<><Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button><Button variant={danger ? 'danger' : 'primary'} disabled={!password || code.trim().length < 6} loading={busy} onClick={() => { setError(null); setBusy(true); run(password, code.trim()).then(onClose, setError).finally(() => setBusy(false)); }}>{confirm}</Button></>}>
      <div className="grid gap-4">
        {error != null && <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">{msg(error, t('errors.INTERNAL_ERROR'))}</p>}
        <Field label={t('sec.currentPassword')} required>{(id) => <Input id={id} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />}</Field>
        <Field label={t('sec.codeOrRecovery')} required>{(id) => <Input id={id} autoComplete="one-time-code" className="tnum" value={code} onChange={(e) => setCode(e.target.value)} />}</Field>
      </div>
    </Dialog>
  );
}

function RecoveryDialog({ codes, onClose }: { codes: string[] | null; onClose: () => void }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (codes) setSaved(false); }, [codes]);
  return (
    <Dialog open={!!codes} onOpenChange={(o) => { if (!o && saved) onClose(); }} title={t('sec.recoveryTitle')} description={t('sec.recoveryOnce')}
      footer={<Button variant="primary" disabled={!saved} onClick={onClose}>{t('common.close')}</Button>}>
      <div className="space-y-4">
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">{codes?.map((c) => <li key={c} className="rounded-md border border-border bg-surface-2 px-3 py-1.5 text-center font-mono text-sm tracking-wide">{c}</li>)}</ul>
        {codes && <CopyButton value={codes.join('\n')} />}
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="size-4 accent-[var(--accent)]" checked={saved} onChange={(e) => setSaved(e.target.checked)} />{t('sec.recoverySaved')}</label>
      </div>
    </Dialog>
  );
}
